import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { AnomalyApiError } from '@app/features/anomalies/anomaly-presentation';
import { applyMarkdown, MarkdownFormat } from '@app/features/anomalies/markdown-editing';
import { formatDateTime, parisNow } from '@app/features/anomalies/paris-time';
import {
  ALL_ROLES,
  atLeastOneRole,
  bounds,
  nextRoleSelection,
  realRoles,
  RoleOption,
  validPeriod,
  withAllRoles,
} from '@app/features/anomalies/targeting';
import { findTour, rolesWithoutSteps, toursByDomain } from '@app/features/tours/tours';
import { UserIdToUserNamePipe } from '@app/features/users/user-id-to-user-name.pipe';
import { userRoleLabel } from '@app/features/users/user-role-labels';
import { realmRoles, UserRole } from '@app/features/users/user.model';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { LucideBold, LucideClock, LucideItalic, LucideLink, LucideList } from '@lucide/angular';
import moment, { Moment } from 'moment';
import { map } from 'rxjs';
import { AnnouncementBanner } from '../announcement-banner/announcement-banner';
import {
  AnnouncementAction,
  announcementAudience,
  announcementErrorMessage,
} from '../announcement-presentation';
import { Announcement, AnnouncementRequest, BannerAnnouncement } from '../announcement.model';
import { AnnouncementService } from '../announcement.service';

export interface AnnouncementDialogData {
  /** L'annonce à modifier, ou à relire si elle est archivée ; absente pour en publier une. */
  announcement?: Announcement;
}

/**
 * Annoncer une nouveauté, la modifier ou l'archiver (RG-nouveaute-annonce) : un titre, un texte en
 * Markdown, le parcours du catalogue à proposer, les rôles cibles et une période facultative, saisis
 * comme ceux d'une anomalie.
 */
@Component({
  selector: 'app-announcement-dialog',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltip,
    SaveButton,
    AnnouncementBanner,
    UserIdToUserNamePipe,
    LucideBold,
    LucideItalic,
    LucideList,
    LucideLink,
    LucideClock,
  ],
  templateUrl: './announcement-dialog.html',
  styleUrls: ['../../anomalies/anomaly-dialog/anomaly-dialog.scss', './announcement-dialog.scss'],
})
export class AnnouncementDialog {
  private readonly dialogRef = inject(MatDialogRef<AnnouncementDialog>);
  private readonly announcementService = inject(AnnouncementService);
  protected readonly announcement = inject<AnnouncementDialogData>(MAT_DIALOG_DATA).announcement;
  protected readonly readOnly = !!this.announcement?.archived;

  protected readonly roles = realmRoles;
  protected readonly ALL_ROLES = ALL_ROLES;
  protected readonly roleLabel = userRoleLabel;
  protected readonly formatDateTime = formatDateTime;
  protected readonly tourGroups = toursByDomain();

  readonly form = new FormGroup(
    {
      title: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
      content: new FormControl('', { nonNullable: true, validators: Validators.required }),
      tourId: new FormControl<string | null>(null, Validators.required),
      targetRoles: new FormControl<RoleOption[]>([], {
        nonNullable: true,
        validators: atLeastOneRole,
      }),
      startDate: new FormControl<Moment | null>(null),
      startTime: new FormControl<string | null>(null),
      endDate: new FormControl<Moment | null>(null),
      endTime: new FormControl<string | null>(null),
    },
    { validators: validPeriod },
  );

  private readonly value = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.getRawValue())),
    { initialValue: this.form.getRawValue() },
  );
  private readonly valid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), {
    initialValue: this.form.valid,
  });

  protected readonly canSave = computed(() => this.valid() && !this.readOnly);
  protected readonly confirmArchive = signal(false);
  /** Le refus de l'API, dit en mots. */
  protected readonly error = signal<string | null>(null);

  protected readonly rolesHint = computed(
    () => `${realRoles(this.value().targetRoles).length} rôles sur ${realmRoles.length}`,
  );
  protected readonly rolesTrigger = computed(() => {
    const roles = realRoles(this.value().targetRoles);
    return roles.length === realmRoles.length
      ? 'Tous les rôles'
      : roles.map((role) => userRoleLabel(role)).join(', ');
  });

  protected readonly audience = computed(() => {
    const value = this.value();
    return announcementAudience(
      { targetRoles: realRoles(value.targetRoles), ...bounds(value) },
      parisNow(),
    );
  });

  /**
   * Les rôles ciblés à qui le parcours n'est pas proposé, aucune de ses étapes ne valant pour eux :
   * ils verraient l'annonce sans « Découvrir ».
   */
  protected readonly rolesWithoutTour = computed(() => {
    const value = this.value();
    const tour = value.tourId ? findTour(value.tourId) : undefined;
    return tour
      ? rolesWithoutSteps(tour, realRoles(value.targetRoles)).map((r) => userRoleLabel(r))
      : [];
  });

  /** Le bandeau tel que les utilisateurs le verront. */
  protected readonly preview = computed<BannerAnnouncement>(() => {
    const value = this.value();
    const { startDate, startTime } = bounds(value);
    return {
      title: value.title || 'Titre de l’annonce',
      content: value.content,
      displayedFrom: startDate
        ? `${startDate}T${startTime ?? '00:00'}:00`
        : (this.announcement?.displayedFrom ?? parisNow()),
    };
  });

  private readonly saveButton = viewChild(SaveButton);
  private readonly contentArea = viewChild<ElementRef<HTMLTextAreaElement>>('contentArea');
  private previousRoles: RoleOption[] = [];

  constructor() {
    if (this.announcement) {
      const roles = withAllRoles(this.announcement.targetRoles);
      this.form.setValue({
        title: this.announcement.title,
        content: this.announcement.content,
        tourId: this.announcement.tourId,
        targetRoles: roles,
        startDate: this.announcement.startDate ? moment.utc(this.announcement.startDate) : null,
        startTime: this.announcement.startTime?.slice(0, 5) ?? null,
        endDate: this.announcement.endDate ? moment.utc(this.announcement.endDate) : null,
        endTime: this.announcement.endTime?.slice(0, 5) ?? null,
      });
      this.previousRoles = roles;
    }
    if (this.readOnly) {
      this.form.disable();
    }
  }

  /** Les rôles cibles retenus, sans l'entrée « Tous les rôles ». */
  selectedRoles(): UserRole[] {
    return realRoles(this.form.controls.targetRoles.value);
  }

  /** « Tous les rôles » coche ou décoche tout ; retirer un rôle le décoche (RG-anomalie-ciblage-par-role). */
  protected onRolesChange(selected: RoleOption[]): void {
    const next = nextRoleSelection(this.previousRoles, selected);
    this.previousRoles = next;
    this.form.controls.targetRoles.setValue(next);
  }

  protected format(format: MarkdownFormat): void {
    const textarea = this.contentArea()?.nativeElement;
    if (!textarea) return;
    const edit = applyMarkdown(
      textarea.value,
      textarea.selectionStart,
      textarea.selectionEnd,
      format,
    );
    this.form.controls.content.setValue(edit.value);
    this.form.controls.content.markAsDirty();
    textarea.value = edit.value;
    textarea.focus();
    textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.form.valid || this.readOnly) return;
    const value = this.form.getRawValue();
    const request: AnnouncementRequest = {
      title: value.title.trim(),
      content: value.content,
      targetRoles: realRoles(value.targetRoles),
      tourId: value.tourId!,
      ...bounds(value),
    };
    this.error.set(null);
    this.saveButton()?.startSaving();
    const save$ = this.announcement
      ? this.announcementService.update(this.announcement.id, request)
      : this.announcementService.create(request);
    save$.subscribe({
      next: (announcement) => {
        this.saveButton()?.stopSaving(true);
        this.dialogRef.close(announcement);
      },
      error: (error) => {
        this.saveButton()?.stopSaving(false);
        this.showError(error, 'save');
      },
    });
  }

  /** Une annonce ne se supprime pas, elle s'archive (RG-nouveaute-annonce). */
  onArchive(): void {
    if (!this.announcement) return;
    if (!this.confirmArchive()) {
      this.confirmArchive.set(true);
      return;
    }
    this.error.set(null);
    this.announcementService.archive(this.announcement.id).subscribe({
      next: (announcement) => this.dialogRef.close(announcement),
      error: (error) => this.showError(error, 'archive'),
    });
  }

  private showError(error: AnomalyApiError, action: AnnouncementAction): void {
    this.error.set(announcementErrorMessage(error ?? {}, action));
  }
}
